'use client'

import headerBgCleartrip from '@/src/assets/png/ai-onboarding-header-cleartrip.png'
import headerBgFlyin from '@/src/assets/png/ai-onboarding-header-flyin.png'
import { isCleartrip } from '@/src/utils/generalUtils/isCleartrip'
import type { SmartSRPMessages } from '@/src/utils/srp/aiRecommendation/locale'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined'
import EventIcon from '@mui/icons-material/Event'
import FlightIcon from '@mui/icons-material/Flight'
import KeyboardVoiceIcon from '@mui/icons-material/KeyboardVoice'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import TuneIcon from '@mui/icons-material/Tune'
import { Box, Chip, Stack, Typography } from '@mui/material'
import Image from 'next/image'

type AIOnboardingVisualKey = 'yellow-blue' | 'blue-orange'

const AI_ONBOARDING_VISUAL: Record<
    AIOnboardingVisualKey,
    { primary: string; primaryDark: string; accent: string; iconTint: string; chipBg: string }
> = {
    'yellow-blue': {
        primary: '#184E92',
        primaryDark: '#0f3468',
        accent: '#D8C969',
        iconTint: 'rgba(24,78,146,0.08)',
        chipBg: 'rgba(24,78,146,0.05)'
    },
    'blue-orange': {
        primary: '#3366CC',
        primaryDark: '#244a99',
        accent: '#F36821',
        iconTint: 'rgba(243,104,33,0.10)',
        chipBg: 'rgba(51,102,204,0.05)'
    }
}

const AI_ONBOARDING_VARIANT: AIOnboardingVisualKey = isCleartrip() ? 'blue-orange' : 'yellow-blue'
export const obVisual = AI_ONBOARDING_VISUAL[AI_ONBOARDING_VARIANT]

export type SRPFloatingAIOnboardingCopy = SmartSRPMessages['floatingAIOnboarding']

const SRP_AI_ONBOARDING_CAPABILITY_ICONS = [
    FlightIcon,
    SwapHorizIcon,
    EventIcon,
    TuneIcon,
    ChatBubbleOutlineOutlinedIcon,
    KeyboardVoiceIcon
] as const

type OnboardingHeaderProps = {
    copy: SRPFloatingAIOnboardingCopy
    aiChatModeTitle: string
    compact: boolean
    flushTop?: boolean
    onboardingHeaderHeight: number
    onDismiss: () => void
}

export const OnboardingHeader = ({
    copy,
    aiChatModeTitle,
    compact,
    flushTop = false,
    onboardingHeaderHeight,
    onDismiss
}: OnboardingHeaderProps) => (
    <Box
        sx={{
            position: 'relative',
            width: '100%',
            minWidth: 0,
            flexShrink: 0,
            minHeight: onboardingHeaderHeight,
            ...(compact
                ? flushTop
                    ? { height: onboardingHeaderHeight, overflow: 'hidden' }
                    : { mt: 1, height: onboardingHeaderHeight, borderRadius: 3, overflow: 'hidden' }
                : { height: onboardingHeaderHeight, overflow: 'hidden' })
        }}
    >
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', }}>
            <Image
                src={isCleartrip() ? headerBgCleartrip : headerBgFlyin}
                alt=""
                fill
                sizes="(max-width: 900px) 100vw, 900px"
                style={{ objectFit: 'cover' }}
            />
        </Box>
        <Box
            sx={{
                position: 'absolute',
                inset: 0,
                opacity: compact ? 0.3 : 0.35,
                mixBlendMode: 'overlay',
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.7) 1px, transparent 0)',
                backgroundSize: compact ? '16px 16px' : '18px 18px'
            }}
        />
        <Box
            sx={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to top, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 60%)'
            }}
        />

        {/* <IconButton
            onClick={onDismiss}
            size="small"
            aria-label={copy.closeAriaLabel}
            sx={{
                position: 'absolute',
                top: compact ? 8 : 12,
                insetInlineEnd: compact ? 8 : 12,
                bgcolor: 'rgba(255,255,255,0.18)',
                color: '#fff',
                backdropFilter: 'blur(8px)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
            }}
        >
            <CloseIcon fontSize="small" />
        </IconButton> */}

        <Stack
            direction="row"
            spacing={compact ? 1.25 : 1.5}
            sx={{
                alignItems: compact ? 'center' : 'flex-end',
                position: 'absolute',
                bottom: compact ? 14 : 18,
                insetInlineStart: compact ? 14 : 24,
                insetInlineEnd: compact ? 14 : 24
            }}
        >
            <Box
                sx={{
                    width: compact ? 42 : 48,
                    height: compact ? 42 : 48,
                    borderRadius: compact ? 2 : 2.5,
                    bgcolor: 'rgba(255,255,255,0.95)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.18)'
                }}
            >
                <AutoAwesomeIcon sx={{ color: obVisual.primary, fontSize: compact ? 22 : 24 }} />
            </Box>
            <Box sx={{ pb: compact ? 0 : 0.5, minWidth: 0, textAlign: 'start' }}>
                <Typography
                    variant={compact ? undefined : 'h6'}
                    sx={{
                        color: '#fff',
                        fontWeight: 700,
                        lineHeight: 1.2,
                        textShadow: '0 1px 2px rgba(0,0,0,0.25)',
                        ...(compact ? { fontSize: 16 } : {})
                    }}
                >
                    {compact ? copy.titleCompact : copy.title}
                </Typography>
                <Stack
                    direction="row"
                    spacing={compact ? 0.75 : 1}
                    sx={{
                        alignItems: 'center',
                        mt: 0.5
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{ color: 'rgba(255,255,255,0.9)', fontSize: compact ? 11 : undefined }}
                    >
                        {aiChatModeTitle}
                    </Typography>
                    <Chip
                        label={copy.betaLabel}
                        size="small"
                        sx={{
                            height: compact ? 16 : 18,
                            fontSize: compact ? 9 : 10,
                            fontWeight: 700,
                            letterSpacing: 0.6,
                            bgcolor: 'rgba(255,255,255,0.22)',
                            color: '#fff',
                            backdropFilter: 'blur(6px)'
                        }}
                    />
                </Stack>
            </Box>
        </Stack>
    </Box>
)

type OnboardingIntroProps = {
    copy: SRPFloatingAIOnboardingCopy
}

export const OnboardingIntro = ({ copy }: OnboardingIntroProps) => (
    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: { xs: 13.5, md: undefined } }}>
        {copy.intro}
    </Typography>
)

type OnboardingCapabilitiesSectionProps = {
    copy: SRPFloatingAIOnboardingCopy
    compact: boolean
}

export const OnboardingCapabilitiesSection = ({ copy, compact }: OnboardingCapabilitiesSectionProps) => (
    <Box
        sx={{
            mt: compact ? 2 : 2.5,
            p: compact ? 2 : 2.5,
            borderRadius: compact ? 2.5 : 3,
            bgcolor: '#f5f7fb',
            border: '1px solid',
            borderColor: 'rgba(15,27,61,0.06)'
        }}
    >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: compact ? 1.25 : 1.5, fontSize: compact ? 13 : undefined }}>
            {copy.whatYouCanDo}
        </Typography>
        {compact ? (
            <Stack spacing={1.1}>
                {SRP_AI_ONBOARDING_CAPABILITY_ICONS.map((Icon, index) => {
                    const label = copy.capabilities[index]
                    return (
                        <Stack key={label} direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                            <Box
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 1.5,
                                    bgcolor: obVisual.iconTint,
                                    color: obVisual.primary,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}
                            >
                                <Icon sx={{ fontSize: 16 }} />
                            </Box>
                            <Typography sx={{ fontSize: 13, color: 'text.primary', lineHeight: 1.35 }}>{label}</Typography>
                        </Stack>
                    )
                })}
            </Stack>
        ) : (
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 1.25
                }}
            >
                {SRP_AI_ONBOARDING_CAPABILITY_ICONS.map((Icon, index) => {
                    const label = copy.capabilities[index]
                    return (
                        <Stack key={label} direction="row" spacing={1.25} sx={{ alignItems: 'flex-start' }}>
                            <Box
                                sx={{
                                    mt: '2px',
                                    width: 26,
                                    height: 26,
                                    borderRadius: 1.5,
                                    bgcolor: obVisual.iconTint,
                                    color: obVisual.primary,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}
                            >
                                <Icon sx={{ fontSize: 15 }} />
                            </Box>
                            <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.4 }}>
                                {label}
                            </Typography>
                        </Stack>
                    )
                })}
            </Box>
        )}
    </Box>
)

type OnboardingTryAskingDesktopProps = {
    copy: SRPFloatingAIOnboardingCopy
}

export const OnboardingTryAskingDesktop = ({ copy }: OnboardingTryAskingDesktopProps) => (
    <Box sx={{ mt: 2.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
            {copy.tryAsking}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {copy.examplePrompts.map(s => (
                <Chip
                    key={s}
                    label={`"${s}"`}
                    variant="outlined"
                    sx={{
                        borderRadius: 999,
                        fontSize: 12,
                        color: 'text.secondary',
                        borderColor: 'rgba(15,27,61,0.12)',
                        pointerEvents: 'none'
                    }}
                />
            ))}
        </Box>
    </Box>
)

type OnboardingTryAskingMobileProps = {
    copy: SRPFloatingAIOnboardingCopy
}

export const OnboardingTryAskingMobile = ({ copy }: OnboardingTryAskingMobileProps) => (
    <Box sx={{ mt: 2.25 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 13, color: 'text.primary', mb: 1 }}>{copy.tryAsking}</Typography>
        <Stack spacing={1}>
            {copy.examplePrompts.map(s => (
                <Box
                    key={s}
                    sx={{
                        px: 1.5,
                        py: 1.25,
                        borderRadius: 2,
                        bgcolor: obVisual.chipBg,
                        border: '1px solid',
                        borderColor: 'rgba(15,27,61,0.08)'
                    }}
                >
                    <Typography sx={{ fontSize: 13, color: 'text.primary' }}>“{s}”</Typography>
                </Box>
            ))}
        </Stack>
    </Box>
)
